import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogoLockup } from './LogoLockup';
import { useAuthStore } from '../store/authStore';
import { usePipelineStore } from '../store/pipelineStore';
import { useCreditsStore } from '../store/creditsStore';
import { api } from '../api/client';
import { useReadinessStore } from '../store/readinessStore';
import { ReadinessScorecard } from './ReadinessScorecard';
import { useReadinessScore } from '../hooks/useReadinessScore';
// WelcomeModal replaced by /welcome page
import { useSubscription } from '../hooks/useSubscription';
import { SUBSCRIPTION_TIERS, SubscriptionTierKey } from '../store/creditsStore';
import { LayoutGrid, Zap, BookOpen, Menu, X, Check, Square, Star, CheckCircle } from 'lucide-react';

const NAV_ICON: Record<string, React.ReactNode> = {
  '/startups': <LayoutGrid size={16} />,
  '/pipeline': <Zap size={16} />,
  '/course': <BookOpen size={16} />,
};

const NAV_ITEMS = [
  { href: '/startups', label: 'My Startups' },
  { href: '/pipeline', label: 'Pipeline' },
  { href: '/course', label: 'Learn' },
];

interface LayoutProps {
  children: React.ReactNode;
}

const STEP_LABELS = ['Profile', 'Idea', 'Reality', 'Positioning', 'Validation'];

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [readinessPopover, setReadinessPopover] = useState(false);
  const readinessPopoverRef = useRef<HTMLDivElement>(null);
  const { allPipelines, loadPipelines } = usePipelineStore();
  const [startupCount, setStartupCount] = useState(0);
  const [firstStartupId, setFirstStartupId] = useState<string | null>(null);
  const [hasFinancials, setHasFinancials] = useState(false);
  const [hasValidationResults, setHasValidationResults] = useState(false);
  const [hasIntelModule, setHasIntelModule] = useState(false);
  const [hasBetaUsers, setHasBetaUsers] = useState(false);
  const { openPanel: openReadiness } = useReadinessStore();
  const { score: readinessScore, barColor: progressBarColor, isReady, completedCount, totalCount: readinessTotal } = useReadinessScore();
  const { credits, fetchBalance, loaded: creditsLoaded } = useCreditsStore();
  const { isSubscribed, plan } = useSubscription();

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    loadPipelines();
    fetchBalance();
    // Fetch startup data for readiness progress
    api.get('/startups').then(({ data }) => {
      if (cancelled) return;
      const startups = data.data.startups ?? [];
      setStartupCount(startups.length);
      if (startups.length > 0) setFirstStartupId(startups[0].id);
      setHasFinancials(startups.some((s: { id: string }) =>
        localStorage.getItem(`sf_has_financials_${s.id}`) === '1'
      ));
      // Check for validation results, intelligence modules, and beta users
      setHasValidationResults(startups.some((s: Record<string, unknown>) =>
        !!(s as any).validationResults?.loggedAt || !!(s as any).validationResults?.submittedAt
      ));
      setHasIntelModule(startups.some((s: Record<string, unknown>) =>
        !!(s as any).intelligence?.nameValidation || !!(s as any).intelligence?.keywords ||
        !!(s as any).intelligence?.competitors || !!(s as any).intelligence?.trends || !!(s as any).intelligence?.sentiment
      ));
      setHasBetaUsers(startups.some((s: Record<string, unknown>) =>
        ((s as any).betaCount ?? 0) > 0
      ));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close readiness popover on outside click
  useEffect(() => {
    if (!readinessPopover) return;
    const handler = (e: MouseEvent) => {
      if (readinessPopoverRef.current && !readinessPopoverRef.current.contains(e.target as Node)) {
        setReadinessPopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [readinessPopover]);

  const activePipeline = allPipelines.active;
  const completedPipelines = allPipelines.completed;
  const hasPipelineCompleted = completedPipelines.length > 0;

  // BUG 1 FIX: Use readinessScore from the unified hook (same source as scorecard)
  const progressPct = readinessScore;

  // Welcome modal replaced by /welcome page — keep sf_welcomed check for backward compat

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#050510', position: 'relative' }}>
      {/* ── Ambient background: fixed orbs + dot grid ── */}
      <div aria-hidden style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {/* Dot grid */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.45) 1px, transparent 1px)',
          backgroundSize: '32px 32px', opacity: 0.07,
        }} />
        {/* Purple orb — top left */}
        <div style={{
          position: 'absolute', width: 900, height: 900, borderRadius: '50%',
          top: -400, left: -300,
          background: 'radial-gradient(circle, rgba(124,111,247,0.1) 0%, transparent 68%)',
          animation: 'orbFloat1 30s ease-in-out infinite',
        }} />
        {/* Orange orb — bottom right */}
        <div style={{
          position: 'absolute', width: 700, height: 700, borderRadius: '50%',
          bottom: -200, right: -200,
          background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)',
          animation: 'orbFloat2 38s ease-in-out infinite',
        }} />
        {/* Cyan orb — mid left */}
        <div style={{
          position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          top: '40%', left: '10%',
          background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)',
          animation: 'orbFloat3 22s ease-in-out infinite',
        }} />
      </div>

      {/* ── Content layer ── */}
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ReadinessScorecard />
      {/* Top navigation */}
      <nav className="no-print" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(5,5,16,0.82)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/startups" className="flex items-center">
              <LogoLockup size="md" />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.startsWith(item.href)
                      ? 'bg-brand-500/20 text-brand-500'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {NAV_ICON[item.href]}
                  {item.label}
                  {item.href === '/pipeline' && activePipeline && (
                    <span style={{
                      background: 'linear-gradient(135deg, #7c6ff7, #00d4ff)',
                      color: '#fff',
                      borderRadius: 10,
                      padding: '1px 7px',
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.3,
                    }}>
                      {activePipeline.status === 'completed' || activePipeline.currentStep >= 5 ? 'Complete' : `${STEP_LABELS[Math.min(activePipeline.currentStep, STEP_LABELS.length - 1)]} ${Math.min(activePipeline.currentStep, 5)}/5`}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* User menu + onboarding progress */}
            <div className="flex items-center gap-3">
              {/* Readiness progress badge — clickable to open popover checklist */}
              {!isReady && progressPct > 0 && (
                <div ref={readinessPopoverRef} className="hidden md:block" style={{ position: 'relative' }}>
                  <button
                    onClick={() => setReadinessPopover(prev => !prev)}
                    className="flex items-center gap-2"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, transition: 'background 0.15s' }}
                    title={`Readiness: ${progressPct}%${readinessTotal > 0 ? ` — ${completedCount}/${readinessTotal} items complete` : ''}`}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(124,111,247,0.08)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    <div style={{ width: 56, height: 5, background: '#1e1e30', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        background: progressBarColor,
                        width: `${progressPct}%`, transition: 'width 0.6s ease',
                      }} />
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: 11 }}>
                      {`${progressPct}% ready`}
                    </span>
                  </button>
                  {readinessPopover && (() => {
                    const { checklist } = useReadinessStore.getState();
                    return (
                      <div style={{
                        position: 'absolute', top: 40, right: 0,
                        background: '#0d0f1a', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 14, padding: 16, width: 288,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 50,
                        animation: 'fadeIn 0.15s ease',
                      }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Your Readiness Checklist</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {checklist.map(item => (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                              <span style={{ flexShrink: 0 }}>{item.done ? <CheckCircle size={14} style={{ color: '#22c55e' }} /> : <Square size={14} style={{ color: '#4b5563' }} />}</span>
                              <span style={{
                                fontSize: 12, flex: 1,
                                color: item.done ? '#6b7280' : '#fff',
                                textDecoration: item.done ? 'line-through' : 'none',
                              }}>{item.label}</span>
                              {!item.done && item.link && (
                                <button
                                  onClick={() => { setReadinessPopover(false); navigate(item.link); }}
                                  style={{
                                    background: 'none', border: 'none', fontSize: 11,
                                    color: '#f97316', cursor: 'pointer', whiteSpace: 'nowrap', padding: 0,
                                  }}
                                >Do it →</button>
                              )}
                            </div>
                          ))}
                        </div>
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 10, paddingTop: 10 }}>
                          <button
                            onClick={() => { setReadinessPopover(false); openReadiness(); }}
                            style={{
                              background: 'none', border: 'none', fontSize: 11,
                              color: '#7c6ff7', cursor: 'pointer', padding: 0,
                            }}
                          >View full scorecard →</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {isReady && (
                <span className="hidden md:block" style={{
                  background: 'rgba(0,230,118,0.12)', color: '#00e676',
                  borderRadius: 8, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                  border: '1px solid rgba(0,230,118,0.3)',
                }}>
                  <Check size={12} /> Market Ready
                </span>
              )}
              {/* Credit balance pill */}
              {creditsLoaded && (
                <button
                  onClick={() => navigate('/credits')}
                  className="hidden md:flex items-center gap-1.5"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 20,
                    padding: '4px 12px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                  title="View credits"
                >
                  {isSubscribed ? (
                    <>
                      <Star size={13} style={{ color: '#f97316' }} />
                      <span style={{ color: '#f97316', fontWeight: 700 }}>{plan && SUBSCRIPTION_TIERS[plan as SubscriptionTierKey] ? SUBSCRIPTION_TIERS[plan as SubscriptionTierKey].name : 'Pro'}</span>
                      <span style={{ color: '#6b7280' }}>·</span>
                      <span style={{ color: '#fff' }}>{Math.max(0, credits)} cr</span>
                    </>
                  ) : (
                    <>
                      <Zap size={13} style={{ color: '#f97316' }} />
                      <span style={{
                        color: Math.max(0, credits) > 10 ? '#fff' : Math.max(0, credits) > 0 ? '#eab308' : '#ef4444',
                      }}>
                        {Math.max(0, credits)} credits
                      </span>
                      {credits <= 0 && (
                        <span style={{ color: '#f97316', fontSize: 11 }}>Buy →</span>
                      )}
                    </>
                  )}
                </button>
              )}
              <span className="hidden md:flex items-center gap-1 text-sm text-gray-400">
                {isSubscribed && <Star size={12} style={{ color: '#f97316' }} />}
                {user?.name}
              </span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-white/5"
              >
                Sign out
              </button>
              {/* Mobile menu toggle */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 text-gray-400 hover:text-white"
              >
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden px-4 py-3 space-y-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5"
              >
                {NAV_ICON[item.href]}
                {item.label}
              </Link>
            ))}
            <Link
              to="/credits"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5"
            >
              <Zap size={16} />
              Credits ({Math.max(0, credits)})
            </Link>
          </div>
        )}
      </nav>

      {/* Page content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
      </div> {/* end content layer */}
    </div>
  );
}
